import './AppMonitorList.scss';

import { AsyncStateNotification } from '@ui/notifications';
import { coreActions } from '@sonar-software/sonar-core';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { MonitorCard } from '@security/monitors/MonitorCard';
import { PlusOutlined } from '@ant-design/icons';
import { SonarCard } from '@ui/general';
import { SonarText } from '@ui/text';
import { useParams } from 'react-router-dom';

import { Button, Space } from 'antd';
import { COPY_GENERIC, SETTINGS_DATA_SECURITY_COPY } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors, createSonarSelector } from '@lib/selectors';
import { Else, If, Then, When } from 'react-if';
import { getMenuItems, monitorMenuConfigs } from '@security/monitors/monitor-form/MonitorMenu';
import React, { useMemo } from 'react';
import {
	useAction,
	useAsyncState,
	useBoolean,
	useCurrentUser,
	useFeatureFlags,
	useOnMount,
	useOnUnmount
} from '@hooks';

type MonitorListRouteParams = {
	id: string;
};

export const postMonitorSelectors = createSonarSelector((state) => state.core.dataSecurity.monitors.post);
export const patchMonitorSelectors = createSonarSelector((state) => state.core.dataSecurity.monitors.byId.patch);
export const deleteMonitorSelectors = createSonarSelector((state) => state.core.dataSecurity.monitors.byId.delete);
const dataSecurityMonitorSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitors.application.get);
const applicationSelector = createAsyncSelectors((state) => state.core.dataSecurity.applications.byId.get);

export const AppMonitorsList: React.FC = () => {
	const { id } = useParams<MonitorListRouteParams>();
	const applicationId = Number(id);
	const loadDataSecurityApplication = useAction(coreActions.requestGetCoreDataSecurityApplicationsById);
	const loadDataSecurityAppMonitors = useAction(coreActions.requestGetCoreDataSecurityMonitorsByApplication);
	const resetDataSecurityAppMonitors = useAction(coreActions.resetGetCoreDataSecurityMonitorsByApplication);
	const { data: applicationData } = useAsyncState(applicationSelector);
	const { data: monitorData, isRequested } = useAsyncState(dataSecurityMonitorSelectors);
	const { isReadOnly } = useCurrentUser();
	const [ isAdding, setIsAdding ] = useBoolean(false);
	const { isEnabled } = useFeatureFlags();

	const menuItemsCount = getMenuItems(
		monitorData ?? [],
		isEnabled,
		[ applicationId ],
		'applicationRequired'
	).filter((x) => !x.disabled)?.length ?? 0;

	const initialMonitors = useMemo(() =>
		monitorData?.filter((monitor) => {
			const flag = monitorMenuConfigs
				.find((config) => config.type === monitor.monitorConfig.monitorTypeId)?.featureFlag;

			return flag ? isEnabled(flag) : true;
		}), [ isEnabled, monitorData ]);

	useOnMount(() => {
		loadDataSecurityApplication({ applicationId });
		loadDataSecurityAppMonitors({ applicationId });
	});

	useOnUnmount(() => {
		resetDataSecurityAppMonitors();
	});

	return (
		<>
			<SonarCard
				className='app-monitor-settings'
				title={ SETTINGS_DATA_SECURITY_COPY.EDIT_MONITOR_SETTINGS(applicationData?.name ?? '') }>
				<div className='app-monitor-list'>
					<SonarText
						size={ 20 }
						weight='semibold'>
						{ SETTINGS_DATA_SECURITY_COPY.MONITORS }
					</SonarText>
					<LoadingSpinner spinning={ isRequested || !monitorData }>
						<Space
							direction='vertical'
							style={ { width: '100%' } }>
							{ initialMonitors?.map((monitor) =>
								<MonitorCard
									key={ monitor.id }
									monitor={ monitor } />) }
							<When condition={ !isReadOnly && menuItemsCount > 0 }>
								<If condition={ isAdding }>
									<Then>
										<MonitorCard
											monitor={ { applicationId: applicationId } }
											onCancel={ setIsAdding.off }
											onSave={ setIsAdding.off } />
									</Then>
									<Else>
										<Button
											className='add-monitor-button'
											icon={ <PlusOutlined /> }
											onClick={ setIsAdding.on }>
											{ SETTINGS_DATA_SECURITY_COPY.ADD_NEW_MONITOR }
										</Button>
									</Else>
								</If>
							</When>
						</Space>
					</LoadingSpinner>
				</div>
			</SonarCard>
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ SETTINGS_DATA_SECURITY_COPY.MONITOR_SAVED }
				resetRawAction={ coreActions.resetPutCoreTagsById }
				stateSelector={ postMonitorSelectors } />
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ SETTINGS_DATA_SECURITY_COPY.MONITOR_DELETED }
				resetRawAction={ coreActions.resetPutCoreTagsById }
				stateSelector={ deleteMonitorSelectors } />
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ COPY_GENERIC.CONTACT_SUPPORT }
				received={ SETTINGS_DATA_SECURITY_COPY.MONITOR_SAVED }
				resetRawAction={ coreActions.resetPutCoreTagsById }
				stateSelector={ patchMonitorSelectors } />
		</>
	);
};
